import React, { useState } from 'react';
import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Popover,
  Select,
  Switch,
  Typography,
} from 'antd';
import {
  createUnderlyingAsset,
  instrumentTypes,
  REGIONS,
  selectors,
  UnderlyingAsset,
  uploadBulkNewAssets,
  uploadNews,
} from 'state';
import { useDispatch, useSelector } from 'react-redux';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  CDS_FREQUECIES,
  TENOR_UNITS,
} from '../../../../state/app/repos/constants';
import { auctions_tenors } from '../../../../state/app/auctions/types';
import SimplifiedOptionAssetFields from './SimplifiedOptionAssetFields';

type Props = {
  hide: () => void;
};

const requiredRule = (label: string) => [
  { required: true, message: `${label} is required.` },
];

const ASSET_CLASSES_WITHOUT_MARKET_HOURS = [
  'Repo',
  'CDS',
  'FX',
  'FxForwards',
  'Crypto',
  'Yields',
  'TBill',
];

const CreateAssetModal: React.FC<Props> = ({ hide }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [investmentThemeForm] = Form.useForm();
  const currencies = useSelector(selectors.currencies);
  const assets = useSelector(selectors.underlyingAssetsList);
  const marketHours = useSelector(selectors.marketHoursItems);
  const marketHoursFetching = useSelector(selectors.marketHoursFetching);
  const fxAsset = assets.filter(asset => asset.resourcetype === 'FX');
  const [assetClass, setAssetClass] = useState<
    UnderlyingAsset['resourcetype'] | undefined
  >(undefined);
  const notForex = assetClass !== 'FX' && assetClass !== 'FxForwards';
  const HTMorAFS = assetClass === 'HTMBond' || assetClass === 'AFSBond';
  const isYields = assetClass === 'Yields';
  const isOption = assetClass === 'Option';
  const isSimplifiedOption = assetClass === 'SimplifiedOption';
  const requiresMarketHours =
    assetClass !== undefined &&
    !ASSET_CLASSES_WITHOUT_MARKET_HOURS.includes(assetClass);

  const [data, setData] = useState(new FormData());
  const [fileSelected, setFileSelected] = useState(false);

  const uploadHandler = async () => {
    const file = data.get('new_assets_file') as File;

    function readFileAsText(file: File) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
        reader.readAsText(file);
      });
    }

    const fileContent = await readFileAsText(file);
    console.log('fileContent: ', fileContent);
    console.log('typeof fileContent: ', typeof fileContent);
    dispatch(uploadBulkNewAssets({ new_assets_file: fileContent }));
  };

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files as FileList;
    const formData = new FormData();
    if (raw[0]) formData.append('new_assets_file', raw[0], raw[0].name);
    setData(formData);
    setFileSelected(true);
  };

  return (
    <Modal
      open={true}
      // onOk={hide}
      onCancel={hide}
      title={'Create a new asset'}
      width={600}
      footer={null}
    >
      <div>
        <Form
          form={form}
          labelCol={{ span: 5 }}
          onFinish={() => {
            const data = form.getFieldsValue();
            if (data['asset_issue_date'] === '')
              data['asset_issue_date'] = null;
            if (data['asset_maturity_date'] === '')
              data['asset_maturity_date'] = null;
            dispatch(createUnderlyingAsset(data));
            // hide();
          }}
          initialValues={{
            visible: true,
          }}
        >
          <Form.Item
            label="Asset Class"
            name="resourcetype"
            rules={requiredRule('Asset Class')}
          >
            <Select
              allowClear
              showSearch
              filterOption={(inputValue, option) => {
                const lowerInput = inputValue.toLowerCase();
                return String(option?.label ?? option?.children ?? '')
                  .toLowerCase()
                  .includes(lowerInput);
              }}
              onChange={value => {
                setAssetClass(value as UnderlyingAsset['resourcetype']);
                if (value === 'SimplifiedOption') {
                  form.setFieldsValue({
                    option_type: 0,
                    issued_delta: 25,
                    contract_multiplier: 100,
                  });
                }
              }}
            >
              {instrumentTypes.map(instrument => {
                return (
                  <Select.Option key={instrument.type} value={instrument.type}>
                    {instrument.type}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item label="Name" name="name" rules={requiredRule('Name')}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Ticker"
            name="ticker"
            rules={requiredRule('Ticker')}
          >
            <Input />
          </Form.Item>
          {HTMorAFS && (
            <Form.Item label="ISIN" name="isin">
              <Input />
            </Form.Item>
          )}
          <Form.Item label="RIC" name="ric">
            <Input />
          </Form.Item>

          <Form.Item label="Region" name="region">
            <Select
              allowClear
              showSearch
              filterOption={(inputValue, option) => {
                const lowerInput = inputValue.toLowerCase();
                return String(option?.label ?? option?.children ?? '')
                  .toLowerCase()
                  .includes(lowerInput);
              }}
            >
              {Object.entries(REGIONS).map(([region, name]) => {
                return (
                  <Select.Option key={region} value={region}>
                    {name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          {notForex && (
            <Form.Item
              label="Geography"
              name="geography"
              rules={[
                ...(requiresMarketHours ? requiredRule('Geography') : []),
                {
                  max: 25,
                  message: 'Geography must be 25 characters or fewer.',
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                loading={marketHoursFetching}
                notFoundContent={
                  marketHoursFetching
                    ? 'Loading geographies…'
                    : marketHours.length === 0
                      ? 'No geographies available. Reload Admin or add one under Geography/Market Hours.'
                      : 'No matching geographies.'
                }
                filterOption={(inputValue, option) => {
                  const lowerInput = inputValue.toLowerCase();
                  return String(option?.label ?? option?.children ?? '')
                    .toLowerCase()
                    .includes(lowerInput);
                }}
              >
                {marketHours
                  .slice()
                  .sort((a, b) => a.geography.localeCompare(b.geography))
                  .map(({ id, geography }) => (
                    <Select.Option key={id} value={geography}>
                      {geography}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            label="Base CCY"
            name="base_currency"
            rules={requiredRule('Base CCY')}
          >
            <Select
              allowClear
              showSearch
              filterOption={(inputValue, option) => {
                const lowerInput = inputValue.toLowerCase();
                return String(option?.label ?? option?.children ?? '')
                  .toLowerCase()
                  .includes(lowerInput);
              }}
            >
              {Object.values(currencies).map(currency => {
                return (
                  <Select.Option key={currency.id} value={currency.id}>
                    {currency.symbol}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            label="Var CCY"
            name="variable_currency"
            rules={requiredRule('Var CCY')}
          >
            <Select
              disabled={isSimplifiedOption}
              allowClear
              showSearch
              filterOption={(inputValue, option) => {
                const lowerInput = inputValue.toLowerCase();
                return String(option?.label ?? option?.children ?? '')
                  .toLowerCase()
                  .includes(lowerInput);
              }}
            >
              {Object.values(currencies).map(currency => {
                return (
                  <Select.Option key={currency.id} value={currency.id}>
                    {currency.symbol}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item label="Issue date / ipo" name="asset_issue_date">
            <Input placeholder={'yyyy-mm-dd'} />
          </Form.Item>
          <Form.Item label="Maturity Date" name="asset_maturity_date">
            <Input placeholder={'yyyy-mm-dd'} />
          </Form.Item>
          <Form.Item valuePropName="checked" label="Visible" name="visible">
            <Switch />
          </Form.Item>
          {isOption && (
            <div>
              {/*<Form.Item label="Tenor" name="tenor_days">*/}
              {/*  <Input placeholder={'input no. of days'} />*/}
              {/*</Form.Item>*/}
              <Form.Item label="Options DB ID:" name="corresponding_id">
                <Input
                  placeholder={'corresponding id of asset from options db '}
                />
              </Form.Item>
              <Form.Item label="PM Underlying:" name="non_derivate_asset">
                <Input
                  placeholder={
                    'this will be a searchable list, for now enter id'
                  }
                />
              </Form.Item>
            </div>
          )}
          {isSimplifiedOption && (
            <SimplifiedOptionAssetFields form={form} assets={assets} />
          )}
          {isYields && (
            <Form.Item label="Tenor" name="tenor">
              <Select allowClear showSearch>
                {/*{Object.entries(REGIONS).map(([region, name]) => {*/}
                {Object.entries(auctions_tenors).map(([region, name]) => {
                  return (
                    <Select.Option key={region} value={region}>
                      {name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}
          {HTMorAFS && (
            <>
              <Divider />
              <Form.Item label="Frequency" name="frequency">
                <Input
                  placeholder={
                    'for semi annual input 6, for anual input 12, etc.'
                  }
                />
              </Form.Item>
              <Form.Item label="Coupon" name="coupon">
                <Input placeholder={'input as 4.75 for a 4.75% coupon'} />
              </Form.Item>
              <Divider />
            </>
          )}
          {assetClass === 'FxForwards' && (
            <>
              <Form.Item
                label={
                  <>
                    No. of decimals
                    <Popover
                      content={
                        <p>
                          For prices with 4 decimals, swap points will be
                          divided by 10000. For prices with 2 decimals, swap
                          points will be divided by 100
                        </p>
                      }
                    >
                      <InfoCircleOutlined />
                    </Popover>
                  </>
                }
                name="no_of_decimals"
                rules={requiredRule('No. of decimals')}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Corresponding FX: "
                name="corresponding_fx_asset"
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(inputValue, option) => {
                    const lowerInput = inputValue.toLowerCase();
                    return String(option?.label ?? option?.children ?? '')
                      .toLowerCase()
                      .includes(lowerInput);
                  }}
                >
                  {Object.values(fxAsset).map(asset => {
                    return (
                      <Select.Option key={asset.id} value={asset.id}>
                        {asset.ric}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </>
          )}
          {assetClass === 'Equity' && (
            <>
              <Form.Item label="Sector" name="sector">
                <Input />
              </Form.Item>
              <Form.Item label="Industry" name="industry">
                <Input />
              </Form.Item>
            </>
          )}
          {assetClass == 'FX' && (
            <>
              <Form.Item label="Description" name="description">
                <Input />
              </Form.Item>
              <Form.Item
                label="Temp name"
                name="temp_name"
                rules={requiredRule('Temp name')}
              >
                <Input />
              </Form.Item>
            </>
          )}
          {(assetClass == 'GovtBond' ||
            assetClass == 'CorporateBond' ||
            assetClass == 'HTMBond' ||
            assetClass == 'AFSBond') && (
            <>
              <Form.Item
                label="Country"
                name="country"
                rules={requiredRule('Country')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Country code"
                name="country_code"
                rules={requiredRule('Country code')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Issuer type"
                name="issuer_type"
                rules={requiredRule('Issuer type')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Credit quality"
                name="credit_quality"
                rules={requiredRule('Credit quality')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Credit rating"
                name="credit_rating"
                rules={requiredRule('Credit rating')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Issuer"
                name="issuer"
                rules={requiredRule('Issuer')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Sector"
                name="sector"
                rules={requiredRule('Sector')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Industry"
                name="industry"
                rules={requiredRule('Industry')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Market classification"
                name="market_classification"
                rules={requiredRule('Market classification')}
              >
                <Input allowClear />
              </Form.Item>
            </>
          )}
          {assetClass === 'Repo' && (
            <>
              please only input positive integers
              <Form.Item
                label="Tenor"
                name="tenor"
                rules={requiredRule('Tenor')}
              >
                <Input allowClear placeholder={'2'} />
              </Form.Item>
              <Form.Item
                label="Tenor unit"
                name="tenor_unit"
                rules={requiredRule('Tenor unit')}
              >
                <Select
                  placeholder={'weeks'}
                  allowClear
                  showSearch
                  filterOption={(inputValue, option) => {
                    const lowerInput = inputValue.toLowerCase();
                    return String(option?.label ?? option?.children ?? '')
                      .toLowerCase()
                      .includes(lowerInput);
                  }}
                >
                  {Object.entries(TENOR_UNITS).map(([unit, name]) => {
                    return (
                      <Select.Option key={unit} value={unit}>
                        {name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </>
          )}
          {assetClass === 'CDS' && (
            <>
              please only input positive integers (no. of years)
              <Form.Item
                label="Maturity"
                name="cds_maturity"
                rules={requiredRule('Maturity')}
              >
                <Input allowClear placeholder={'5'} />
              </Form.Item>
              <Form.Item
                label="Frequency"
                name="cds_frequency"
                rules={requiredRule('Frequency')}
              >
                <Select
                  placeholder={'semi-annually'}
                  allowClear
                  showSearch
                  filterOption={(inputValue, option) => {
                    const lowerInput = inputValue.toLowerCase();
                    return String(option?.label ?? option?.children ?? '')
                      .toLowerCase()
                      .includes(lowerInput);
                  }}
                >
                  {Object.entries(CDS_FREQUECIES).map(([unit, name]) => {
                    return (
                      <Select.Option key={unit} value={unit}>
                        {name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </>
          )}
          {assetClass === 'Futures' && (
            <>
              <Form.Item label="Lot size" name="lot_size">
                <Input allowClear placeholder={'1000'} />
              </Form.Item>
              <Form.Item label="Lot unit" name="lot_unit">
                <Input allowClear placeholder={'oil barrels'} />
              </Form.Item>
            </>
          )}
          {assetClass?.includes('ETF') && (
            <>
              <Form.Item
                label="Classification"
                name="classification"
                rules={requiredRule('Classification')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item label="Objectives" name="objectives">
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Temp name"
                name="temp_name"
                rules={requiredRule('Temp name')}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item label="ESG score" name="esg_score">
                <Select allowClear showSearch>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(value => {
                    return (
                      <Select.Option key={value} value={value}>
                        {value}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Form.Item label="Top holdings" name="top_holdings">
                <Input allowClear />
              </Form.Item>
              <Form.Item label="Links" name="links">
                <Input allowClear />
              </Form.Item>
            </>
          )}
          {!assetClass?.includes('ETF') && (
            <Form.Item>
              <div
                style={{ display: 'flex', justifyContent: 'right', gap: 10 }}
              >
                <Button type="primary" htmlType="submit">
                  Create
                </Button>
                <Button onClick={hide}>Cancel</Button>
              </div>
            </Form.Item>
          )}
        </Form>
        {assetClass?.includes('ETF') && (
          <Form
            labelCol={{ span: 5 }}
            form={investmentThemeForm}
            onFinish={async investmentThemeValues => {
              try {
                // The ETF submit button lives in this second form, so validate
                // the asset fields explicitly before building the combined payload.
                const data = await form.validateFields();
                data['investment_theme'] = investmentThemeValues;
                const top_holdings_str = data['top_holdings'];
                const links_str = data['links'];
                data['top_holdings'] = top_holdings_str
                  ? top_holdings_str.split(',')
                  : [];
                data['links'] = links_str ? links_str.split(',') : [];
                if (data['asset_issue_date'] === '')
                  data['asset_issue_date'] = null;
                if (data['asset_maturity_date'] === '')
                  data['asset_maturity_date'] = null;
                dispatch(createUnderlyingAsset(data));
                hide();
              } catch {
                // validateFields renders the actionable messages beside each field.
              }
            }}
          >
            <Divider> Investment Theme</Divider>
            <Form.Item label="Category" name="category">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Inverse" name="inverse">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Leveraged" name="leveraged">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Asset Class" name="asset_class">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Asset Class Size" name="asset_class_size">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Asset Class Styles" name="asset_class_style">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Sector General" name="sector_general">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Sector specific" name="sector_specific">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Sector" name="sector">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Region General" name="region_general">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Region Specific" name="region_specific">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Commodity Type" name="commodity_type">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Commodity" name="commodity">
              <Input allowClear />
            </Form.Item>
            <Form.Item label="Commodity Exposure" name="commodity_exposure">
              <Input allowClear />
            </Form.Item>
            <Form.Item>
              <div
                style={{ display: 'flex', justifyContent: 'right', gap: 10 }}
              >
                <Button type="primary" htmlType="submit">
                  Create
                </Button>
                <Button onClick={hide}>Cancel</Button>
              </div>
            </Form.Item>
          </Form>
        )}
      </div>
      <Divider>
        {<Typography.Text type={'secondary'}>or bulk upload</Typography.Text>}
      </Divider>
      <form id={'new_assets_form'}>
        {/*<Popover*/}
        {/*  placement={'right'}*/}
        {/*  // content={infoHover}*/}
        {/*  title=".csv header must look like:"*/}
        {/*>*/}
        {/*  <InfoCircleOutlined />*/}
        {/*</Popover>*/}
        <Button
          disabled={!fileSelected}
          style={{ marginLeft: 10 }}
          onClick={uploadHandler}
        >
          Upload
        </Button>
        <input
          style={{ marginLeft: '8px', marginBottom: '12px' }}
          type="file"
          id="new_assets_file"
          name="new_assets_file"
          accept=".csv"
          onChange={e => handleFileSelection(e)}
        />
      </form>
    </Modal>
  );
};
export default CreateAssetModal;
